import React, { useRef, useEffect, useMemo } from 'react';
import { useFullScreenHandle } from 'react-full-screen'
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Stack } from '@mui/material';
import { PMTILES_PREF_KEY, FOR_COLOR_TEST } from '../config/config'
import { PMTILES_URL } from '../const/general'
import { RISK_ANALYSIS_LAYER_KEY } from '../const/map_config'
import {
    RAIN_H1_LEVEL_POINT,
    RAIN_H24_LEVEL_POINT,
    RAIN_H48_LEVEL_POINT,
    RISK_LEVEL_POINT,
    BSN_RISK_LEVEL_POINT,
    RAIN_COLOR,
    TANK_LEVEL_POINT,
} from '../const/color'
import { setRiskColor, setRainColor, setSrRiskColor } from '../func/color';
import bbox from '@turf/bbox';
import { BoxCenterStart } from '../style/box'
import { StyleFullScreen, MapWrapper } from '../style/map_components';
import '../style/legend_map.css';
import MapTimeSlider from '../item/slider_map'
import SliderTimePanel from '../item/slider_time_panel'
import MapFullscreenIcon from '../item/map_fullscreen_icon'
import choropleth_city from '../data/city.json';

const MapRiskAnalysis = (props) => {
    const sourceSetting = useMemo(() => {
        return {
            "osm": {
                "type": "raster",
                "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
                "tileSize": 256,
                "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
            },
            "city": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/city/{z}/{x}/{y}.mvt`]
            },
            "bsn": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/bsn/{z}/{x}/{y}.mvt`]
            },
            "h1_msh": {
                "type": "vector",
                "tiles": FOR_COLOR_TEST
                    ? [`${PMTILES_URL}/${PMTILES_PREF_KEY}/h1_msh/{z}/{x}/{y}.mvt`]
                    : [`${PMTILES_URL}/forecast/h1_msh/{z}/{x}/{y}.mvt`]
            },
            "msh_3rd": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/msh_3rd/{z}/{x}/{y}.mvt`]
            },
            "msh": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/msh/{z}/{x}/{y}.mvt`]
            }
        };
    }, [props.strDateTimePath, props.prefCode]);

    // 背景地図の設定
    const baseLayer = [
        {
            "id": "osm-tiles",
            "type": "raster",
            "source": "osm",
            "minzoom": 0,
            "maxzoom": 24
        }
    ];

    // PMTilesから取得して表示、非表示を制御するレイヤ達  
    const otherLayers = [
        {
            "id": "bsn_outline",
            "source": "bsn",
            "source-layer": "bsn",
            "type": "line",
            "paint": {
                'line-color': '#ffffff',     // 線の色
                'line-width': [            // 枠線ように太めに設定する
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    3, 2.4,
                    12, 4.4,
                    20, 8.4
                ],
                'line-opacity': 1,         // 不透明度
                'line-dasharray': [2, 0],    // 点線パターン（例: [2px 線, 4px 空白]）
                'line-offset': 0             // 線のオフセット（上下位置ずらし）
            },
            "layout": {
                "visibility": "none" // 最初から表示する場合は "visible"に
            }
        },
        {
            "id": "bsn",
            "source": "bsn",
            "source-layer": "bsn",
            "type": "line",
            "paint": {
                'line-color': '#5c9fed',     // 線の色
                'line-width': [            // 線の太さ（ピクセル）
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    3, 2,
                    12, 4,
                    20, 8
                ],
                'line-opacity': 1,         // 不透明度
                'line-dasharray': [2, 0],    // 点線パターン（例: [2px 線, 4px 空白]）
                'line-offset': 0             // 線のオフセット（上下位置ずらし）
            },
            "layout": {
                "visibility": "none" // 最初から表示する場合は "visible"に
            }
        },
        {
            "id": "h1_msh",
            "source": "h1_msh",
            "source-layer": "h1_msh",
            "type": "fill",
            "paint": {
                "fill-color": "#5c9fed",
                "fill-opacity": 0.7,
                'fill-outline-color': 'rgba(0,0,0,0)'
            },
            "layout": {
                "visibility": "none" // 最初から表示する場合は "visible"に
            }
        },
        {
            "id": "msh_3rd",
            "source": "msh_3rd",
            "source-layer": "msh",
            "type": "fill",
            "paint": {
                "fill-color": 'rgba(0,0,0,0)',
                "fill-opacity": 0.7,
                'fill-outline-color': 'rgba(0,0,0,0)'
            },
            "layout": {
                "visibility": "none" // 最初から表示する場合は "visible"に
            }
        },
        {
            "id": "msh",
            "source": "msh",
            "source-layer": "msh",
            "type": "fill",
            "paint": {
                "fill-color": 'rgba(0,0,0,0)',
                "fill-opacity": 0.7,
                'fill-outline-color': 'rgba(0,0,0,0)'
            },
            "layout": {
                "visibility": "visible" // 最初から表示する場合は "visible"に
            }
        },
        {
            "id": "city",
            "source": "city",
            "source-layer": "city",
            "type": "line",
            "paint": {
                'line-opacity': 0.8,
                'line-color': '#FFFFFF',
                'line-width': 1.5
            },
            "layout": {
                "visibility": "visible" // 最初から表示する場合は "visible"に
            }
        },
    ];

    const mapContainer = useRef(null);
    const map = useRef(null);
    const markerRef = useRef(null);

    // 監視用Ref
    const selectCitysRef = useRef(props.selectCitys);
    const mapDataRef = useRef(props.mapData);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto',
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let hoveredId = null

    const setLayerColor = (val) => {
        switch (props.selectLayer.category) {
            case RISK_ANALYSIS_LAYER_KEY.MSH_H1:
                return setRainColor(RAIN_H1_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.MSH_H24:
                return setRainColor(RAIN_H24_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.MSH_H48:
                return setRainColor(RAIN_H48_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.MSH_RISK:
                return setRiskColor(RISK_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.MSH_SOIL:
                return setSrRiskColor(val)?.color
            case RISK_ANALYSIS_LAYER_KEY.BSN_H1:
                return setRainColor(RAIN_H1_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.BSN_H24:
                return setRainColor(RAIN_H24_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.BSN_H48:
                return setRainColor(RAIN_H48_LEVEL_POINT, val)
            case RISK_ANALYSIS_LAYER_KEY.BSN_OF:
                return setRiskColor(BSN_RISK_LEVEL_POINT, val)
            default:
                return setRainColor(RAIN_H1_LEVEL_POINT, val)
        }
    }

    const getLayerName = () => {
        switch (props.selectLayer.category) {
            case RISK_ANALYSIS_LAYER_KEY.MSH_H1:
                return { layer_name: 'h1_msh', points: RAIN_H1_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.MSH_H24:
                return { layer_name: 'msh_3rd', points: RAIN_H24_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.MSH_H48:
                return { layer_name: 'msh_3rd', points: RAIN_H48_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.MSH_RISK:
                return { layer_name: 'msh', points: RISK_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.MSH_SOIL:
                return { layer_name: 'msh', points: TANK_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.BSN_H1:
                return { layer_name: 'bsn', points: RAIN_H1_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.BSN_H24:
                return { layer_name: 'bsn', points: RAIN_H24_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.BSN_H48:
                return { layer_name: 'bsn', points: RAIN_H48_LEVEL_POINT }
            case RISK_ANALYSIS_LAYER_KEY.BSN_OF:
                return { layer_name: 'bsn', points: BSN_RISK_LEVEL_POINT }
            default:
                return { layer_name: 'h1_msh', points: RAIN_H1_LEVEL_POINT }
        }
    }

    const eventOn = (layerName) => {
        map.current.setLayoutProperty(layerName,
            'visibility', 'visible');
        map.current.on('mouseenter', layerName, (e) => { handleMouseMove(e) });
        map.current.on('mousemove', layerName, (e) => { handleMouseMove(e) });
        map.current.on('mouseleave', layerName, handleMouseLeave);
        map.current.on('click', layerName, handleClick);
    }
    const eventOff = (layerName) => {
        map.current.setLayoutProperty(layerName,
            'visibility', 'none');
        map.current.off('mouseenter', layerName, (e) => { handleMouseMove(e) });
        map.current.off('mousemove', layerName, (e) => { handleMouseMove(e) });
        map.current.off('mouseleave', layerName, handleMouseLeave);
        map.current.off('click', layerName, handleClick);
    }

    // レイヤー切り替え
    const switchLayer = () => {
        if (map.current && map.current.isStyleLoaded()) {
            if (props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_H1) {
                // 1時間雨量が選択されている場合
                map.current.setLayoutProperty('h1_msh',
                    'visibility', 'visible');
                map.current.setLayoutProperty('city',
                    'visibility', 'visible');
                map.current.setLayoutProperty('bsn_outline',
                    'visibility', 'none');
                map.current.setLayoutProperty('msh_3rd',
                    'visibility', 'visible');
                map.current.on('mouseenter', 'msh_3rd', (e) => { handleMouseMove(e) });
                map.current.on('mousemove', 'msh_3rd', (e) => { handleMouseMove(e) });
                map.current.on('mouseleave', 'msh_3rd', handleMouseLeave);
                map.current.on('click', 'msh_3rd', handleClick);
            } else {
                // 1時間雨量が選択解除された場合
                map.current.setLayoutProperty('h1_msh',
                    'visibility', 'none');
            }
            if (props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_H24
                || props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_H48
            ) {
                // 1時間雨量、24時間雨量,48時間雨量,土砂リスクが選択されている場合
                eventOn('msh_3rd')
                map.current.setLayoutProperty('city',
                    'visibility', 'visible');
                map.current.setLayoutProperty('msh',
                    'visibility', 'none');
                map.current.setLayoutProperty('bsn_outline',
                    'visibility', 'none');
            } else {
                // 1時間雨量、24時間雨量,48時間雨量,土砂リスクが選択解除された場合
                eventOff('msh_3rd')
            }
            if (props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_RISK
                || props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_SOIL
            ) {
                // 1時間雨量、24時間雨量,48時間雨量,土砂リスクが選択されている場合
                eventOn('msh')
                map.current.setLayoutProperty('city',
                    'visibility', 'visible');
                map.current.setLayoutProperty('bsn_outline',
                    'visibility', 'none');
            } else {
                // 1時間雨量、24時間雨量,48時間雨量,土砂リスクが選択解除された場合
                eventOff('msh')
            }
            if (props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.BSN_H1
                || props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.BSN_H24
                || props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.BSN_H48
                || props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.BSN_OF
            ) {
                // 24時間雨量,48時間雨量,土砂リスクが選択されている場合
                eventOn('bsn')
                map.current.setLayoutProperty('city',
                    'visibility', 'none');
                map.current.setLayoutProperty('msh',
                    'visibility', 'none');
                map.current.setLayoutProperty('bsn_outline',
                    'visibility', 'visible');
            } else {
                // 24時間雨量,48時間雨量,土砂リスクが選択解除された場合
                eventOff('bsn')
            }
            if (props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_H1) {
                // 1時間雨量が選択されている場合、すべてのイベント登録が終わった後に
                // メッシュのクリックイベントだけ追加する
                map.current.setLayoutProperty('msh_3rd',
                    'visibility', 'visible');
                map.current.on('click', 'msh_3rd', handleClick);
            }
        }
    }

    // ベースレイヤーとなる市町村のラインを設定
    const setBaseCityLayer = () => {
        const matchExpression_line = ['match', ['get', 'id']];
        if (!selectCitysRef.current || selectCitysRef.current === undefined || selectCitysRef.current === null || selectCitysRef.current.length < 1) {
            map.current.setPaintProperty('city', 'line-color', 'rgba(0,0,0,0)');
            return
        }
        for (const item of selectCitysRef.current) {
            matchExpression_line.push(item, '#FFFFFF')
        }
        matchExpression_line.push('rgba(0, 0, 0, 0)');
        map.current.setPaintProperty('city', 'line-color', matchExpression_line);
    }

    // 河川スタイル変更
    const setBsnStyle = (layer_info) => {
        // データがなければ透明化して終了
        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) {
            map.current.setPaintProperty(layer_info.layer_name, 'line-color', 'rgba(0,0,0,0)');
            map.current.setPaintProperty("bsn_outline", 'line-color', 'rgba(0,0,0,0)');
            return;
        }

        // 色設定
        const matchExpression_fill = ['match', ['get', 'bsn_code']];
        const matchExpression_line = ['match', ['get', 'bsn_code']];
        for (const item of mapDataRef.current) {
            if (item.code != null && item.val[props.chooseNum] != null) {
                matchExpression_fill.push(
                    item.code,
                    setLayerColor(item.val[props.chooseNum]));
                matchExpression_line.push(item.code, '#ffffff');
            }
        }

        matchExpression_fill.push('rgba(0,0,0,0)');
        matchExpression_line.push('rgba(0,0,0,0)');
        // 塗り
        map.current.setPaintProperty(layer_info.layer_name, 'line-color', matchExpression_fill);
        map.current.setPaintProperty("bsn_outline", 'line-color', matchExpression_line);
    };

    // メッシュのスタイル変更
    const setMshStyle = (layer_info) => {
        // データがなければ透明化して終了
        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) {
            map.current.setPaintProperty(layer_info.layer_name, 'fill-color', 'rgba(0,0,0,0)');
            return;
        }
        // 色設定
        const matchExpression_fill = ['match', ['get', 'id']];
        for (const item of mapDataRef.current) {
            if (item.code != null && item.val[props.chooseNum] != null) {
                matchExpression_fill.push(
                    item.code,
                    setLayerColor(item.soil ? item.soil[props.chooseNum] : item.val[props.chooseNum]));
            }
        }
        matchExpression_fill.push('rgba(0,0,0,0)');

        // 塗り
        map.current.setPaintProperty(layer_info.layer_name, 'fill-color', matchExpression_fill);
        map.current.setPaintProperty(layer_info.layer_name, 'fill-opacity', 0.7);
    };

    // レイヤーの色分け
    const setLayerStyle = (layer_info) => {

        if (!map.current || !map.current.isStyleLoaded()) return;
        switchLayer()

        // ベースレイヤーとなる市町村のラインを設定
        setBaseCityLayer()

        if (props.selectLayer.category === RISK_ANALYSIS_LAYER_KEY.MSH_H1) {
            map.current.setPaintProperty('msh_3rd', 'fill-opacity', 0);
            map.current.setPaintProperty(layer_info.layer_name, 'fill-color', [
                'case',
                ['==', ['get', 'choose_num'], props.chooseNum],
                [
                    'match',
                    ['get', 'sint_value_cls_num'],
                    0, RAIN_COLOR.LEVEL_1.color,
                    1, RAIN_COLOR.LEVEL_2.color,
                    2, RAIN_COLOR.LEVEL_3.color,
                    3, RAIN_COLOR.LEVEL_4.color,
                    4, RAIN_COLOR.LEVEL_5.color,
                    5, RAIN_COLOR.LEVEL_6.color,
                    6, RAIN_COLOR.LEVEL_7.color,
                    7, RAIN_COLOR.LEVEL_8.color,
                    RAIN_COLOR.OTHER.color
                ],
                'rgba(0,0,0,0)'
            ]);

        } else {
            if (props.selectLayer.category.startsWith('bsn')) {
                setBsnStyle(layer_info)
            } else {
                setMshStyle(layer_info)
            }
        }
    }

    // 地図のセンターとズーム調整
    const setMapCenterZoom = () => {
        if (!map.current || selectCitysRef.current.length === 0) return;

        const features = choropleth_city.features

        // 対象IDの feature を抽出
        const filteredFeatures = features.filter(f =>
            selectCitysRef.current.includes(f.properties.id)
        );

        if (filteredFeatures.length === 0) {
            console.warn("対象フィーチャが見つかりません");
            return;
        }

        // 抽出したフィーチャのコレクションを作成
        const filteredCollection = {
            type: "FeatureCollection",
            features: filteredFeatures
        };

        // Turf.js で bbox を取得
        const bounds = bbox(filteredCollection); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }

    // レイヤーマウスホバー時の処理
    const handleMouseMove = (e) => {
        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.id ?? feature.properties.bsn_code;
        const properties = feature.properties;
        // すでにホバー中なら無視
        if (hoveredId === id) return;

        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) return

        const data = mapDataRef.current.find(item => item.code === Number(id));

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }
        if (!data) {
            map.current.getCanvas().style.cursor = '';
            return;
        } else {
            map.current.getCanvas().style.cursor = 'pointer';
        }
        hoveredId = id;

        // 河川レイヤーなら ポップアップは表示
        if (props.selectLayer.category.startsWith('bsn')) {
            const popup = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false
            })

                .setLngLat(e.lngLat)
                .setHTML(` <div>河川名：<strong>${properties.txt_river_name_show}</strong></div>`)
                .addClassName('custom-popup')
                .addTo(map.current);

            map.current.__hoverPopup = popup;
        }
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };

    // mapクリック時の処理
    const handleClick = (e) => {
        const lngLat = e.lngLat;
        if (markerRef.current) {
            props.setCurrentLngLat([lngLat.lng, lngLat.lat])
            props.setShouldSelectRankTop(false)
        }
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        setLayerStyle(getLayerName())
        map.current.getCanvas().addEventListener('mouseleave', handleMouseLeave);
        const center = map.current.getCenter();
        markerRef.current = new maplibregl.Marker()
            .setLngLat([center.lng, center.lat])
            .addTo(map.current);
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return; // initialize map only once

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.currentLngLat,
            zoom: 9,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true,
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false,
        }), 'bottom-right');

        map.current.on('load', handleLoad);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            map.current.off('mouseenter', 'bsn', (e) => { handleMouseMove(e) });
            map.current.off('mousemove', 'bsn', (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', 'bsn', handleMouseLeave);
            map.current.off('mouseenter', 'msh_3rd', (e) => { handleMouseMove(e) });
            map.current.off('mousemove', 'msh_3rd', (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', 'msh_3rd', handleMouseLeave);
            map.current.off('mouseenter', 'h1_msh', (e) => { handleMouseMove(e) });
            map.current.off('mousemove', 'h1_msh', (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', 'h1_msh', handleMouseLeave);

            map.current.off('styledata', setLayerStyle(getLayerName()));
            map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {
        selectCitysRef.current = props.selectCitys

        if (mapDataRef.current && mapDataRef.current !== undefined && mapDataRef.current !== null && mapDataRef.current.length > 0) {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle(getLayerName());
            }
        }
    }, [props.selectCitys]);

    useEffect(() => {
        if (!map.current || !markerRef.current) return;

        // マーカーだけ先に置いておく
        markerRef.current.setLngLat(props.currentLngLat);

        // 地図をマーカーに合わせて移動（
        map.current.easeTo({
            center: props.currentLngLat,
            duration: 2000,
            easing: t => t * (2 - t), // 緩やかな自然な動き
            essential: true
        });
    }, [props.currentLngLat]);

    // データ更新時
    // 市町村チェックボックス変更時、
    // レイヤー切り替え時
    // 市町村レイヤークリック時
    // 字レイヤークリック時
    // メッシュレイヤークリック時
    // リセットボタンで個別の選択が解除されたときも呼ばれる
    useEffect(() => {
        mapDataRef.current = props.mapData

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                const layer_info = getLayerName()
                if (map.current.getLayer(layer_info.layer_name)) {
                    setLayerStyle(layer_info);
                    clearInterval(interval);
                }
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.mapData, props.chooseNum
    ]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <Stack direction="column"
                sx={{ height: '100%', minHeight: 0 }} >
                <MapWrapper sx={{ flex: 1, minHeight: 0 }}>
                    <div
                        ref={mapContainer}
                        className=""
                        style={mapStyle} />
                    <MapFullscreenIcon
                        fullscreenHandle={fullscreenHandle}
                        top={'10px'}
                    />
                </MapWrapper>
                <BoxCenterStart >
                    <SliderTimePanel
                        selectDateTime={props.dates[props.chooseNum]}
                        chooseNum={props.chooseNum}
                        setChooseNum={props.setChooseNum}
                        dates={props.dates}
                    />
                </BoxCenterStart>
                <Box mr={2}>
                    <MapTimeSlider
                        markIndex={6}
                        chooseNum={props.chooseNum}
                        setChooseNum={props.setChooseNum}
                        dates={props.dates}
                    />
                </Box>
            </Stack>
        </StyleFullScreen>
    );
};

export default MapRiskAnalysis;